import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, apiError } from "@/context/AuthContext";
import { post } from "@/lib/api";
import { Zap, Loader2, GraduationCap, ShieldCheck, User } from "lucide-react";

export default function Auth() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login, register, otpVerify } = useAuth();
  const [mode, setMode] = useState(sp.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState("student");
  const [loginKind, setLoginKind] = useState("email"); // email | userid | otp
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "", user_id: "", ref_code: sp.get("ref") || "" });
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const go = (u) => nav({ student: "/app", teacher: "/teacher", admin: "/admin" }[u.role]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const u = await register({ name: form.name, email: form.email, password: form.password, mobile: form.mobile, role, ref_code: form.ref_code });
        toast.success("Welcome to RankTest!");
        go(u);
      } else if (loginKind === "userid") {
        const u = await login({ user_id: form.user_id, password: form.password });
        go(u);
      } else {
        const u = await login({ email: form.email, password: form.password });
        go(u);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    setBusy(true);
    try {
      const r = await post("/auth/otp/request", { mobile: form.mobile });
      setOtpSent(true);
      toast.success(r.message || "OTP sent");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try { const u = await otpVerify({ mobile: form.mobile, otp }); go(u); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const demo = async (email, password) => {
    setBusy(true);
    try { const u = await login({ email, password }); toast.success(`Logged in as ${u.role}`); go(u); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex rt-gradient text-white flex-col justify-between p-12">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => nav("/")}>
          <div className="h-9 w-9 rounded-xl bg-white/15 flex items-center justify-center"><Zap className="h-5 w-5" /></div>
          <span className="font-display font-extrabold text-2xl">RankTest</span>
        </div>
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-tight">Your rank starts here.</h2>
          <p className="text-indigo-100 mt-4 max-w-md">Access mock tests, live classes, AI practice and analytics trusted by millions of aspirants.</p>
          <div className="mt-8 space-y-3">
            <p className="text-sm font-semibold text-indigo-200">Try instantly with a demo account:</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => demo("student@ranktest.com", "Student@123")} data-testid="demo-student-btn" className="bg-white/10 border border-white/25 rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-white/20"><User className="h-4 w-4" /> Student</button>
              <button onClick={() => demo("ananya.teacher@ranktest.com", "Teacher@123")} data-testid="demo-teacher-btn" className="bg-white/10 border border-white/25 rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-white/20"><GraduationCap className="h-4 w-4" /> Teacher</button>
              <button onClick={() => demo("courierrpsinternational@gmail.com", "Admin@12345")} data-testid="demo-admin-btn" className="bg-white/10 border border-white/25 rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-white/20"><ShieldCheck className="h-4 w-4" /> Admin</button>
            </div>
          </div>
        </div>
        <p className="text-xs text-indigo-200">© 2026 RankTest</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6 cursor-pointer" onClick={() => nav("/")}>
            <div className="h-9 w-9 rounded-xl rt-gradient flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
            <span className="font-display font-extrabold text-xl">RankTest</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{mode === "register" ? "Create account" : "Welcome back"}</h1>
          <p className="text-slate-500 mt-1">{mode === "register" ? "Start your preparation for free." : "Log in to continue your prep."}</p>

          {mode === "register" && (
            <div className="flex gap-2 mt-5">
              {[["student", "Student"], ["teacher", "Teacher"]].map(([r, l]) => (
                <button key={r} onClick={() => setRole(r)} data-testid={`role-tab-${r}`} className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${role === r ? "bg-indigo-800 text-white border-indigo-800" : "border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
            </div>
          )}

          {mode === "login" && (
            <div className="flex gap-2 mt-5 text-xs">
              {[["email", "Email"], ["userid", "User ID"], ["otp", "Mobile OTP"]].map(([k, l]) => (
                <button key={k} onClick={() => setLoginKind(k)} data-testid={`login-kind-${k}`} className={`px-3 py-1.5 rounded-full font-semibold ${loginKind === k ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-500"}`}>{l}</button>
              ))}
            </div>
          )}

          {mode === "login" && loginKind === "otp" ? (
            <div className="mt-5 space-y-3">
              <Field label="Mobile number"><input data-testid="otp-mobile-input" value={form.mobile} onChange={set("mobile")} className="inp" placeholder="9000000099" /></Field>
              {!otpSent ? (
                <button onClick={sendOtp} disabled={busy} data-testid="send-otp-btn" className="btn-primary w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Send OTP"}</button>
              ) : (
                <>
                  <Field label="Enter OTP (dev: 123456)"><input data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value)} className="inp" placeholder="123456" /></Field>
                  <button onClick={verifyOtp} disabled={busy} data-testid="verify-otp-btn" className="btn-primary w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Verify & Login"}</button>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-3">
              {mode === "register" && <Field label="Full name"><input required data-testid="name-input" value={form.name} onChange={set("name")} className="inp" placeholder="Your name" /></Field>}
              {mode === "login" && loginKind === "userid" ? (
                <Field label="User ID"><input required data-testid="userid-input" value={form.user_id} onChange={set("user_id")} className="inp" placeholder="RTXXXXXXXX" /></Field>
              ) : (
                <Field label="Email"><input required type="email" data-testid="email-input" value={form.email} onChange={set("email")} className="inp" placeholder="you@example.com" /></Field>
              )}
              {mode === "register" && <Field label="Mobile"><input data-testid="mobile-input" value={form.mobile} onChange={set("mobile")} className="inp" placeholder="9000000000" /></Field>}
              {mode === "register" && <Field label="Referral code (optional)"><input data-testid="ref-code-input" value={form.ref_code} onChange={set("ref_code")} className="inp" placeholder="REFXXXXXX — you both get a bonus" /></Field>}
              <Field label="Password"><input required type="password" data-testid="password-input" value={form.password} onChange={set("password")} className="inp" placeholder="••••••••" /></Field>
              <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="btn-primary w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : mode === "register" ? "Create account" : "Log in"}</button>
            </form>
          )}

          <p className="text-sm text-slate-500 mt-5 text-center">
            {mode === "register" ? "Already have an account? " : "New to RankTest? "}
            <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="toggle-mode-btn" className="text-indigo-700 font-semibold">{mode === "register" ? "Log in" : "Sign up free"}</button>
          </p>

          <div className="lg:hidden mt-6 border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold text-slate-500 mb-2">Quick demo login:</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => demo("student@ranktest.com", "Student@123")} className="text-xs bg-slate-100 rounded-lg px-3 py-1.5 font-semibold">Student</button>
              <button onClick={() => demo("ananya.teacher@ranktest.com", "Teacher@123")} className="text-xs bg-slate-100 rounded-lg px-3 py-1.5 font-semibold">Teacher</button>
              <button onClick={() => demo("courierrpsinternational@gmail.com", "Admin@12345")} className="text-xs bg-slate-100 rounded-lg px-3 py-1.5 font-semibold">Admin</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .inp { width:100%; border:1px solid #E2E8F0; border-radius:.6rem; padding:.65rem .8rem; font-size:.9rem; outline:none; }
        .inp:focus { border-color:#1E3A8A; box-shadow:0 0 0 3px rgba(30,58,138,.12); }
        .btn-primary { background:#1E3A8A; color:#fff; font-weight:700; padding:.7rem; border-radius:.6rem; font-size:.9rem; }
        .btn-primary:hover { background:#172a63; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span><div className="mt-1">{children}</div></label>;
}
